import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '~/components/ui/sheet'
import { cn } from '~/lib/utils'

/** Largeurs reprises des dialogues migrés (`max-w-xl`, `max-w-3xl`). */
const SIZES = {
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
} as const

type IProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  /** Les actions du formulaire, fixées en bas du tiroir pendant que le corps défile. */
  footer?: React.ReactNode
  size?: keyof typeof SIZES
  children: React.ReactNode
}

/**
 * Tiroir latéral pour les formulaires longs du back-office (#83).
 *
 * En modale centrée, un formulaire long débordait : on faisait défiler
 * l'intérieur de la modale, bouton « Enregistrer » compris. Ici l'en-tête et le
 * pied restent fixes, seul le corps défile, sur toute la hauteur de l'écran ;
 * pleine largeur sur téléphone. Les confirmations courtes restent des modales.
 *
 * Focus piégé, Échap et titre annoncé viennent du `Sheet` (Radix Dialog).
 */
export function SidePanel({ open, onOpenChange, title, description, footer, size = 'md', children }: IProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn('w-full gap-0 p-0', SIZES[size])}
        // Sans description, Radix attend qu'on le dise explicitement.
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <SheetHeader className="border-b border-border p-6 pr-12">
          <SheetTitle className="font-heading text-xl">{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {footer && (
          <SheetFooter className="mt-0 flex-row justify-end border-t border-border p-4">
            {footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
